import {
  useRef,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useViewStore } from '@/stores/useViewStore';

interface SplitViewProps {
  sheetId: string;
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  leftTitle?: string;
  rightTitle?: string;
  minLeftWidth?: number; // percentage
  minRightWidth?: number; // percentage
  className?: string;
}

export function SplitView({
  sheetId,
  leftPanel,
  rightPanel,
  leftTitle = 'Grid',
  rightTitle = 'Gantt',
  minLeftWidth = 20,
  minRightWidth = 20,
  className,
}: SplitViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const {
    getSplitConfig,
    setSplitWidth,
    toggleLeftCollapsed,
    toggleRightCollapsed,
    scrollTop,
    setScrollPosition,
  } = useViewStore();

  const config = getSplitConfig(sheetId);
  const [localWidth, setLocalWidth] = useState(config.leftWidth);

  // Sync local width with store
  useEffect(() => {
    setLocalWidth(config.leftWidth);
  }, [config.leftWidth]);

  // Calculate actual widths
  const getWidths = useCallback(() => {
    if (config.leftCollapsed) {
      return { left: 0, right: 100 };
    }
    if (config.rightCollapsed) {
      return { left: 100, right: 0 };
    }
    return { left: localWidth, right: 100 - localWidth };
  }, [config.leftCollapsed, config.rightCollapsed, localWidth]);

  const widths = getWidths();

  // Handle divider drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;

      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const startX = e.clientX;
      const startWidth = localWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;

        const deltaX = moveEvent.clientX - startX;
        const deltaPercent = (deltaX / containerRect.width) * 100;
        const newWidth = startWidth + deltaPercent;

        // Clamp to min widths
        const clampedWidth = Math.max(
          minLeftWidth,
          Math.min(100 - minRightWidth, newWidth)
        );

        setLocalWidth(clampedWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        setSplitWidth(sheetId, localWidth);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [localWidth, minLeftWidth, minRightWidth, setSplitWidth, sheetId]
  );

  // Synchronized scrolling
  const handleLeftScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      setScrollPosition(target.scrollTop);

      // Sync right panel scroll
      if (rightPanelRef.current && !isDragging.current) {
        rightPanelRef.current.scrollTop = target.scrollTop;
      }
    },
    [setScrollPosition]
  );

  const handleRightScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      setScrollPosition(target.scrollTop);

      // Sync left panel scroll
      if (leftPanelRef.current && !isDragging.current) {
        leftPanelRef.current.scrollTop = target.scrollTop;
      }
    },
    [setScrollPosition]
  );

  // Sync scroll position from store
  useEffect(() => {
    if (leftPanelRef.current) {
      leftPanelRef.current.scrollTop = scrollTop;
    }
    if (rightPanelRef.current) {
      rightPanelRef.current.scrollTop = scrollTop;
    }
  }, [scrollTop]);

  return (
    <div
      ref={containerRef}
      className={cn('flex h-full w-full overflow-hidden', className)}
    >
      {/* Left Panel */}
      <div
        className={cn(
          'relative flex flex-col overflow-hidden transition-all duration-200',
          config.leftCollapsed && 'w-0'
        )}
        style={{
          width: config.leftCollapsed ? 0 : `${widths.left}%`,
          minWidth: config.leftCollapsed ? 0 : `${minLeftWidth}%`,
        }}
      >
        {/* Left Panel Header */}
        {!config.leftCollapsed && (
          <div className="flex h-8 shrink-0 items-center justify-between border-b bg-muted/50 px-2">
            <span className="text-xs font-medium text-muted-foreground">
              {leftTitle}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => toggleLeftCollapsed(sheetId)}
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Collapse {leftTitle}</TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Left Panel Content */}
        <div
          ref={leftPanelRef}
          className="flex-1 overflow-auto"
          onScroll={handleLeftScroll}
        >
          {!config.leftCollapsed && leftPanel}
        </div>
      </div>

      {/* Divider */}
      <div
        className={cn(
          'group relative flex w-1 shrink-0 cursor-col-resize items-center justify-center bg-border hover:bg-primary/20',
          (config.leftCollapsed || config.rightCollapsed) && 'cursor-pointer'
        )}
        onMouseDown={
          config.leftCollapsed || config.rightCollapsed ? undefined : handleMouseDown
        }
        onClick={() => {
          if (config.leftCollapsed) {
            toggleLeftCollapsed(sheetId);
          } else if (config.rightCollapsed) {
            toggleRightCollapsed(sheetId);
          }
        }}
      >
        {/* Collapse/Expand buttons when collapsed */}
        {config.leftCollapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-0 h-8 w-4 rounded-none"
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand {leftTitle}</TooltipContent>
          </Tooltip>
        )}

        {config.rightCollapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 h-8 w-4 rounded-none"
              >
                <ChevronLeft className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Expand {rightTitle}</TooltipContent>
          </Tooltip>
        )}

        {/* Grip indicator when not collapsed */}
        {!config.leftCollapsed && !config.rightCollapsed && (
          <div className="absolute inset-y-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
            <div className="flex h-8 w-4 items-center justify-center rounded bg-primary/10">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        )}
      </div>

      {/* Right Panel */}
      <div
        className={cn(
          'relative flex flex-col overflow-hidden transition-all duration-200',
          config.rightCollapsed && 'w-0'
        )}
        style={{
          width: config.rightCollapsed ? 0 : `${widths.right}%`,
          minWidth: config.rightCollapsed ? 0 : `${minRightWidth}%`,
        }}
      >
        {/* Right Panel Header */}
        {!config.rightCollapsed && (
          <div className="flex h-8 shrink-0 items-center justify-between border-b bg-muted/50 px-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => toggleRightCollapsed(sheetId)}
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Collapse {rightTitle}</TooltipContent>
            </Tooltip>
            <span className="text-xs font-medium text-muted-foreground">
              {rightTitle}
            </span>
          </div>
        )}

        {/* Right Panel Content */}
        <div
          ref={rightPanelRef}
          className="flex-1 overflow-auto"
          onScroll={handleRightScroll}
        >
          {!config.rightCollapsed && rightPanel}
        </div>
      </div>
    </div>
  );
}

export default SplitView;
